import React, { useState, useEffect, useRef } from "react";
import { CiMenuKebab } from "react-icons/ci";
import { IconButton, Menu, MenuItem, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Box } from "@mui/material";
import { Block, Notify } from "notiflix";
import { LoadingButton } from "@mui/lab";
import { isEmpty } from "underscore";
import { appStore, useTimeZoneStore } from "../zustand";
import NoMeeting from "../assets/no_data.svg";
import Placeholder from "../shared/Placeholder";
import { makeRequest } from "../util/AxiosInstance";
import { useInfiniteQuery } from "@tanstack/react-query";
import useCalBooking from "../hooks/useCalBooking";
import { handleError } from "../util/functions";
import moment from "moment-timezone";
import { LuCalendarClock } from "react-icons/lu";

function MeetingItem({ meeting, refresh }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const anchorRef = useRef(null);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false)
  const { rescheduleBooking } = useCalBooking();
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const handleRescheduleClick = () => {
    handleMenuClose();
    setOpenRescheduleModal(true);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRescheduleSubmit = async () => {
    try {
      setIsRescheduling(true)
      const isoDate = new Date(rescheduleDate).toISOString();

      await rescheduleBooking(meeting.uid, {
        start: isoDate,
        reschedulingReason: rescheduleReason
      });
      refresh()
      Notify.success("Booking rescheduled.")
      setOpenRescheduleModal(false);
      setRescheduleDate('');
      setRescheduleReason('');
    } catch (error) {
      const err = handleError(error)
      Notify.failure(err)
      console.error(error);
    } finally {
      setIsRescheduling(false)
    }
  };

  let userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    // Ensure we're working with valid dates
    const startDate = moment(meeting.start);
    const endDate = moment(meeting.end);

    // Convert to local timezone
    let localStartTime = startDate.tz(userTimeZone);
    let localEndTime = endDate.tz(userTimeZone);

    // Format the dates
    let meetingDate = localStartTime.format("dddd, MMMM D");
    let startTime = localStartTime.format("HH:mm A");
    let endTime = localEndTime.format("HH:mm A");

    setDate(meetingDate);
    setStartTime(startTime);
    setEndTime(endTime);
  }, [meeting, userTimeZone]);

  return (
    <div className="meeting-item">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body1" fontWeight={"semibold"}>
          {meeting.title}
        </Typography>
        <div>
          <IconButton
            size="small"
            ref={anchorRef}
            onClick={handleMenuClick}>
            <CiMenuKebab />
          </IconButton>
          <Menu
            anchorEl={anchorRef.current}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              },
            }}
          >
            {/* <MenuItem>Edit</MenuItem> */}
            <MenuItem onClick={handleRescheduleClick}>Reschedule</MenuItem>
          </Menu>
        </div>
      </div>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
        <span><LuCalendarClock fontSize={'12px'} /></span>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <Typography variant="body2" fontWeight={400}>
            {startTime} - {endTime}
          </Typography>
          <Typography variant="caption" fontWeight={300}>
            {date}
          </Typography>
        </Box>
      </Box>
      <Dialog
        className="booking-modal"

        open={openRescheduleModal} onClose={() => setOpenRescheduleModal(false)}>
        <DialogTitle>Reschedule Booking</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Date & Time"
            type="datetime-local"
            fullWidth
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Reason for Rescheduling"
            fullWidth
            multiline
            rows={4}
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRescheduleModal(false)}>Cancel</Button>
          <LoadingButton
            disabled={isRescheduling || rescheduleReason === "" || rescheduleDate === null}
            loading={isRescheduling}
            onClick={handleRescheduleSubmit} variant="contained" color="primary">
            Confirm Reschedule
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Typography variant="body1" sx={{ mt: 1 }}>
        <span>{meeting.time}</span>
        <span>{meeting.date}</span>
      </Typography>
    </div>
  );
}

function MeetingItems() {
  const { mode } = appStore()
  const [take, setTake] = useState(5)
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['bookings', status],
    queryFn: async ({ pageParam = 0 }) => {
      const uri = mode == 'admin' ? "/admin/bookings" : "/cal/bookings"
      const response = await makeRequest(
        "get",
        `${uri}?take=${take}&skip=${pageParam}&status=${'upcoming'}`
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentItems = allPages.reduce((acc, page) => acc + page.data?.length, 0);
      const hasMore = lastPage.data?.length === take;
      return hasMore ? currentItems : undefined;
    },
  });

  useEffect(() => {
    if (isLoading) {
      Block.circle('.meeting-list')
    } else {
      Block.circle('.meeting-list')
      Block.remove('.meeting-list')
    }
  }, [isLoading]);

  return (
    <div className="meeting-list">
      {!isEmpty(data) && 'pages' in data && !isEmpty(data.pages) ?
        data?.pages?.map((page, i) => (
          <React.Fragment key={i}>
            {page.data.map((booking) => (
              <MeetingItem
                refresh={refetch}
                key={booking.id}
                meeting={booking} />
            ))}
          </React.Fragment>
        ))
        : <>
          <Placeholder text={'No meetings found.'} />
        </>}
      {hasNextPage && (
        <LoadingButton
          sx={{
            mx: 'auto'
          }}
          loading={isFetchingNextPage}
          onClick={fetchNextPage}
          variant="text"
          size="small"
          color="primary">
          Load More
        </LoadingButton>
      )}
    </div>
  );
}

export default MeetingItems;